/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Shared utilities for parsing and formatting product metadata from markdown.
 * Product metadata is stored in data/product/product.md within each product's repository.
 * The file is the source of truth; the database serves as a cache for querying.
 */

import * as fs from 'fs';
import * as path from 'path';
import { normalizeLineEndings } from './issues';
import type { Product, InsertProduct } from '../types/products';

// Re-export types for convenience
export type { Product, InsertProduct };

/**
 * Product metadata parsed from markdown file.
 * All fields match the database schema except timestamps which may be strings.
 */
export interface ProductMetadata {
  id: string;                    // UUID - the authoritative identifier
  productId?: string | null;     // Display-only PROD-XXXX format, optional
  organizationId: string;        // Organization UUID
  title: string;
  description: string;
  owner: string;
  repositoryUrl: string | null;
  repoPath: string | null;
  capabilities: string[];
  users: string[];
  links: string[];
  architectureDiagram: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Partial product metadata with optional fields.
 * Used when parsing files that may have missing required fields.
 */
export interface PartialProductMetadata {
  id?: string;
  productId?: string | null;
  organizationId?: string;
  title?: string;
  description?: string;
  owner?: string;
  repositoryUrl?: string | null;
  repoPath?: string | null;
  capabilities: string[];
  users: string[];
  links: string[];
  architectureDiagram?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Result of parsing product metadata from markdown.
 * Provides detailed error information when parsing fails or fields are missing.
 */
export interface ProductParseResult {
  success: boolean;
  metadata?: ProductMetadata;
  partial?: PartialProductMetadata;
  missingFields?: string[];
  error?: string;
}

/**
 * Result of importing a product from a repository
 */
export interface ProductImportResult {
  success: boolean;
  product?: ProductMetadata;
  error?: string;
  needsCreation?: boolean;       // True if no product.md found (and autoCreate was false)
  autoCreated?: boolean;         // True if product was auto-created (no product.md existed)
}

/**
 * Generates a product ID from the product name.
 * Format: PROD-XXXX where XXXX is derived from the first 4 alphanumeric characters.
 * 
 * @param name - Product name to derive ID from
 * @returns Product ID in PROD-XXXX format
 * 
 * @example
 * generateProductIdFromName("Velocity PPM") // "PROD-VELO"
 * generateProductIdFromName("SkillShop") // "PROD-SKIL"
 * generateProductIdFromName("AI") // "PROD-AIXX"
 */
export function generateProductIdFromName(name: string): string {
  // Extract alphanumeric characters only, uppercase
  const chars = name
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 4)
    .padEnd(4, 'X'); // Pad with X if name is too short
  
  return `PROD-${chars}`;
}

/**
 * Parses product metadata from markdown content with detailed error reporting.
 * Use this function when you need to know exactly which fields are missing.
 * 
 * @param content - Markdown content to parse
 * @param options - Optional parsing options
 * @param options.requireOrgId - Whether organizationId is required (default: true for strict mode)
 * @returns Parse result with metadata, partial data, and error details
 */
export function parseProductWithDetails(
  content: string,
  options: { requireOrgId?: boolean } = {}
): ProductParseResult {
  const { requireOrgId = true } = options;
  
  if (!content || typeof content !== 'string') {
    return {
      success: false,
      error: 'No content provided or content is not a string',
    };
  }

  // Normalize line endings to handle Windows CRLF files
  const lines = normalizeLineEndings(content).split('\n');
  
  // Initialize with empty/default values
  const product: PartialProductMetadata = {
    capabilities: [],
    users: [],
    links: [],
  };

  let currentSection: 'main' | 'capabilities' | 'users' | 'links' | null = 'main';
  let titleFromHeader: string | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Parse title from header: # Product: Title (flexible matching)
    const titleMatch = trimmedLine.match(/^#\s+(?:Product:\s*)?(.+)$/);
    if (titleMatch && !titleFromHeader) {
      const headerText = titleMatch[1].trim();
      // If it starts with "Product:" extract the actual title
      const productPrefixMatch = headerText.match(/^Product:\s*(.+)$/i);
      titleFromHeader = productPrefixMatch ? productPrefixMatch[1].trim() : headerText;
      continue;
    }

    // Detect section headers (case-insensitive)
    const sectionHeaderLower = trimmedLine.toLowerCase();
    if (sectionHeaderLower === '## capabilities') {
      currentSection = 'capabilities';
      continue;
    }
    if (sectionHeaderLower === '## user roles' || sectionHeaderLower === '## users') {
      currentSection = 'users';
      continue;
    }
    if (sectionHeaderLower === '## links') {
      currentSection = 'links';
      continue;
    }
    // Any other ## header ends list sections
    if (trimmedLine.startsWith('## ')) {
      currentSection = null;
      continue;
    }

    // Parse list items in sections
    if (currentSection === 'capabilities' || currentSection === 'users' || currentSection === 'links') {
      const listMatch = trimmedLine.match(/^-\s+(.+)$/);
      if (listMatch) {
        const value = listMatch[1].trim();
        if (value) {
          product[currentSection]!.push(value);
        }
      }
      continue;
    }

    // Parse metadata fields: - **Field:** Value (more flexible matching)
    // Supports both "- **Field:** Value" and "- **Field**: Value" (colon outside bold)
    const fieldMatch = line.match(/^-\s+\*\*([^:*]+)\*?\*?:?\*?\*?\s*(.*)$/);
    if (fieldMatch && currentSection === 'main') {
      const [, fieldName, fieldValue] = fieldMatch;
      const normalizedFieldName = fieldName.trim().replace(/:$/, '');
      const value = fieldValue.trim();

      switch (normalizedFieldName.toLowerCase()) {
        case 'id':
          product.id = value || undefined;
          break;
        case 'product id':
          product.productId = value || undefined;
          break;
        case 'organization id':
        case 'org id':
        case 'organizationid':
          product.organizationId = value || undefined;
          break;
        case 'title':
        case 'name':
          product.title = value || undefined;
          break;
        case 'description':
          product.description = value || undefined;
          break;
        case 'owner':
          product.owner = value || undefined;
          break;
        case 'repository url':
        case 'repo url':
        case 'repositoryurl':
          product.repositoryUrl = value || null;
          break;
        case 'repository path':
        case 'repo path':
        case 'repositorypath':
          product.repoPath = value || null;
          break;
        case 'architecture diagram':
          product.architectureDiagram = value || null;
          break;
        case 'created':
        case 'createdat':
        case 'created at':
          product.createdAt = value || undefined;
          break;
        case 'updated':
        case 'updatedat':
        case 'updated at':
          product.updatedAt = value || undefined;
          break;
      }
    }
  }

  // Use header title if no explicit title field
  if (!product.title && titleFromHeader) {
    product.title = titleFromHeader;
  }

  // Check for missing required fields
  const missingFields: string[] = [];
  if (!product.id) missingFields.push('ID');
  if (requireOrgId && !product.organizationId) missingFields.push('Organization ID');
  if (!product.title) missingFields.push('Title');
  if (!product.description) missingFields.push('Description');
  if (!product.owner) missingFields.push('Owner');

  // Set defaults for timestamps if missing
  const now = new Date().toISOString();
  if (!product.createdAt) {
    product.createdAt = now;
  }
  if (!product.updatedAt) {
    product.updatedAt = now;
  }

  // If there are missing required fields, return partial data with error info
  if (missingFields.length > 0) {
    return {
      success: false,
      partial: product,
      missingFields,
      error: `Missing required fields: ${missingFields.join(', ')}`,
    };
  }

  // All required fields present - return full metadata
  return {
    success: true,
    metadata: {
      id: product.id!,
      productId: product.productId,
      organizationId: product.organizationId!,
      title: product.title!,
      description: product.description!,
      owner: product.owner!,
      repositoryUrl: product.repositoryUrl ?? null,
      repoPath: product.repoPath ?? null,
      capabilities: product.capabilities,
      users: product.users,
      links: product.links,
      architectureDiagram: product.architectureDiagram ?? null,
      createdAt: product.createdAt!,
      updatedAt: product.updatedAt!,
    },
  };
}

/**
 * Parses product metadata from markdown content.
 * Expected format matches data/product/product.md structure.
 * 
 * For detailed error information, use parseProductWithDetails() instead.
 * 
 * @param content - Markdown content to parse
 * @returns Parsed product metadata or null if invalid/empty
 */
export function parseProductFromMarkdown(content: string): ProductMetadata | null {
  const result = parseProductWithDetails(content, { requireOrgId: true });
  return result.success ? result.metadata! : null;
}

/**
 * Formats product metadata as markdown content.
 * Output matches the expected data/product/product.md format.
 * 
 * @param product - Product metadata to format
 * @returns Markdown content string
 */
export function formatProductToMarkdown(product: ProductMetadata | Product): string {
  const lines: string[] = [];

  // Header
  lines.push(`# Product: ${product.title}`);
  lines.push('');

  // Metadata fields
  lines.push(`- **ID:** ${product.id}`);
  if (product.productId) {
    lines.push(`- **Product ID:** ${product.productId}`);
  }
  lines.push(`- **Organization ID:** ${product.organizationId}`);
  lines.push(`- **Description:** ${product.description}`);
  lines.push(`- **Owner:** ${product.owner}`);
  lines.push(`- **Repository URL:** ${product.repositoryUrl || ''}`);
  lines.push(`- **Repository Path:** ${product.repoPath || ''}`);
  
  // Format timestamps - handle both Date objects and strings
  const createdAt = product.createdAt instanceof Date 
    ? product.createdAt.toISOString() 
    : product.createdAt;
  const updatedAt = product.updatedAt instanceof Date 
    ? product.updatedAt.toISOString() 
    : product.updatedAt;
  
  lines.push(`- **Created:** ${createdAt}`);
  lines.push(`- **Updated:** ${updatedAt}`);

  // Architecture diagram (if present)
  if (product.architectureDiagram) {
    lines.push(`- **Architecture Diagram:** ${product.architectureDiagram}`);
  }

  lines.push('');

  // Capabilities section
  lines.push('## Capabilities');
  const capabilities = product.capabilities || [];
  if (capabilities.length > 0) {
    for (const capability of capabilities) {
      lines.push(`- ${capability}`);
    }
  }
  lines.push('');

  // User Roles section
  lines.push('## User Roles');
  const users = product.users || [];
  if (users.length > 0) {
    for (const user of users) {
      lines.push(`- ${user}`);
    }
  }
  lines.push('');

  // Links section
  lines.push('## Links');
  const links = product.links || [];
  if (links.length > 0) {
    for (const link of links) {
      lines.push(`- ${link}`);
    }
  }
  lines.push('');

  return lines.join('\n');
}

/**
 * Converts ProductMetadata (from file) to database InsertProduct format.
 * 
 * @param metadata - Parsed product metadata
 * @returns Insert product object for database
 */
export function metadataToInsertProduct(metadata: ProductMetadata): InsertProduct {
  return {
    id: metadata.id,
    organizationId: metadata.organizationId,
    productId: metadata.productId,
    title: metadata.title,
    description: metadata.description,
    owner: metadata.owner,
    links: metadata.links.length > 0 ? metadata.links : undefined,
    capabilities: metadata.capabilities.length > 0 ? metadata.capabilities : undefined,
    users: metadata.users.length > 0 ? metadata.users : undefined,
    repositoryUrl: metadata.repositoryUrl,
    repoPath: metadata.repoPath,
    architectureDiagram: metadata.architectureDiagram,
  };
}

/**
 * Converts database Product to ProductMetadata format.
 * 
 * @param product - Database product record
 * @returns Product metadata for file storage
 */
export function productToMetadata(product: Product): ProductMetadata {
  return {
    id: product.id,
    productId: product.productId,
    organizationId: product.organizationId,
    title: product.title,
    description: product.description,
    owner: product.owner,
    repositoryUrl: product.repositoryUrl,
    repoPath: product.repoPath,
    capabilities: product.capabilities || [],
    users: product.users || [],
    links: product.links || [],
    architectureDiagram: product.architectureDiagram,
    createdAt: product.createdAt instanceof Date 
      ? product.createdAt.toISOString() 
      : product.createdAt,
    updatedAt: product.updatedAt instanceof Date 
      ? product.updatedAt.toISOString() 
      : product.updatedAt,
  };
}

//
// File utilities for product.md
//

/**
 * Gets the path to the product.md file for a given repository.
 * 
 * @param repoPath - Base path to the repository
 * @param productPath - Optional subdirectory within the repo (for monorepos)
 * @returns Full path to product.md
 */
export function getProductFilePath(repoPath: string, productPath?: string | null): string {
  const basePath = productPath 
    ? path.join(repoPath, productPath) 
    : repoPath;
  return path.join(basePath, 'data', 'product', 'product.md');
}

/**
 * Reads and parses the product.md file from a repository.
 * 
 * @param repoPath - Base path to the repository
 * @param productPath - Optional subdirectory within the repo
 * @returns Parsed product metadata or null if file doesn't exist
 */
export function readProductFile(repoPath: string, productPath?: string | null): ProductMetadata | null {
  const filePath = getProductFilePath(repoPath, productPath);
  
  try {
    if (!fs.existsSync(filePath)) {
      return null;
    }
    const content = fs.readFileSync(filePath, 'utf8');
    return parseProductFromMarkdown(content);
  } catch (error) {
    console.error(`Error reading product file at ${filePath}:`, error);
    return null;
  }
}

/**
 * Writes product metadata to the product.md file.
 * Creates the directory structure if it doesn't exist.
 * 
 * @param repoPath - Base path to the repository
 * @param product - Product metadata to write
 * @param productPath - Optional subdirectory within the repo
 */
export function writeProductFile(
  repoPath: string, 
  product: ProductMetadata | Product, 
  productPath?: string | null
): void {
  const filePath = getProductFilePath(repoPath, productPath);
  const dirPath = path.dirname(filePath);
  
  // Ensure directory exists
  if (!fs.existsSync(dirPath)) {
    fs.mkdirSync(dirPath, { recursive: true });
  }
  
  const content = formatProductToMarkdown(product);
  fs.writeFileSync(filePath, content, 'utf8');
}

/**
 * Checks if a product.md file exists in a repository.
 * 
 * @param repoPath - Base path to the repository
 * @param productPath - Optional subdirectory within the repo
 * @returns True if product.md exists
 */
export function productFileExists(repoPath: string, productPath?: string | null): boolean {
  const filePath = getProductFilePath(repoPath, productPath);
  return fs.existsSync(filePath);
}

/**
 * Generates a new UUID for a product.
 * This is a simple implementation; in production you might use a proper UUID library.
 */
export function generateProductUUID(): string {
  // Use crypto if available (Node.js), otherwise fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback UUID v4 generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Creates a new product metadata object with generated IDs.
 * 
 * @param data - Partial product data (at minimum: title, description, owner, organizationId)
 * @returns Complete product metadata with generated IDs and timestamps
 */
export function createProductMetadata(
  data: Pick<ProductMetadata, 'title' | 'description' | 'owner' | 'organizationId'> & Partial<ProductMetadata>
): ProductMetadata {
  const now = new Date().toISOString();
  
  return {
    id: data.id || generateProductUUID(),
    productId: data.productId || generateProductIdFromName(data.title),
    organizationId: data.organizationId,
    title: data.title,
    description: data.description,
    owner: data.owner,
    repositoryUrl: data.repositoryUrl || null,
    repoPath: data.repoPath || null,
    capabilities: data.capabilities || [],
    users: data.users || [],
    links: data.links || [],
    architectureDiagram: data.architectureDiagram || null,
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now,
  };
}

/**
 * Updates product metadata with new values, updating the timestamp.
 * 
 * @param existing - Existing product metadata
 * @param updates - Fields to update
 * @returns Updated product metadata
 */
export function updateProductMetadata(
  existing: ProductMetadata,
  updates: Partial<Omit<ProductMetadata, 'id' | 'createdAt'>>
): ProductMetadata {
  return {
    ...existing,
    ...updates,
    id: existing.id, // Preserve original ID
    createdAt: existing.createdAt, // Preserve creation date
    updatedAt: new Date().toISOString(), // Update timestamp
  };
}

